export interface AuthProductAccess {
  code: string;
  plan: string;
  /** Loaded via GET /auth/me/permissions — omitted from slim JWT. */
  permissions?: string[];
  roleCode?: string;
  levelRank?: number;
  levelName?: string;
}

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  orgId: string;
  role: string;
  isSuperAdmin: boolean;
  products?: AuthProductAccess[];
  /** S3 object keys (private bucket) */
  profileImage?: string | null;
  coverImage?: string | null;
  /** Presigned URLs for display */
  profileImageUrl?: string | null;
  coverImageUrl?: string | null;
}

export interface AuthSession {
  id: string;
  createdAt: string;
  lastAccessed: string;
  expiresAt: string;
  userAgent: string | null;
  ipAddress: string | null;
  /** City / region / country from auth-service (geoip-lite), when known. */
  ipLocation?: string | null;
  deviceName: string | null;
  /** Vendor + model when the UA exposes it. */
  deviceModel?: string | null;
  /** From auth-service UA parsing (when available). */
  browser?: string | null;
  os?: string | null;
  deviceType?: string | null;
  isCurrent?: boolean;
}

export interface LoginResponse {
  accessToken: string;
  /** Returned for SPAs when HttpOnly cookies are not sent cross-origin; store securely. */
  refreshToken?: string;
  sessionId: string;
  tokenType: string;
  expiresIn: number;
  /** Friendly session label from auth-service UA parsing. */
  deviceName?: string | null;
  deviceModel?: string | null;
  browser?: string | null;
  os?: string | null;
  deviceType?: string | null;
  user: AuthUser;
}

export type MfaMethod = 'totp' | 'email';

export interface MfaChallengeResponse {
  mfaRequired: true;
  mfaToken: string;
  methods: MfaMethod[];
  maskedEmail: string;
  emailSent: boolean;
  expiresIn: number;
}

export interface MfaStatus {
  mfaEnabled: boolean;
  totpEnabled: boolean;
  emailEnabled: boolean;
  hasBackupCodes: boolean;
}

export function isMfaChallenge(
  data: LoginResponse | MfaChallengeResponse | undefined,
): data is MfaChallengeResponse {
  return Boolean(data && 'mfaRequired' in data && data.mfaRequired);
}

export interface MeResponse {
  user: AuthUser & {
    sub: string;
    /** Slim product metadata — permission codes via GET /auth/me/permissions. */
    products?: AuthProductAccess[];
  };
}

export interface MyPermissionsResponse {
  products: AuthProductAccess[];
  /** Present when session role-preview is active (exact org-role permissions). */
  rolePreview?: {
    orgRoleId: string;
    orgRoleLevelId: string | null;
    roleName: string;
    roleCode: string | null;
    levelName: string | null;
    levelRank: number | null;
    permissions: string[];
    setAt: string;
  } | null;
}


/** Auth-service `/auth/security-dashboard` */
export interface SecurityActivityItem {
  id: string;
  kind: string;
  title: string;
  meta: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  at: string;
  ipAddress?: string | null;
  location?: string | null;
}

export interface TrustedDevice {
  id: string;
  fingerprint: string;
  deviceName: string | null;
  userAgent: string | null;
  lastIp: string | null;
  lastIpLocation?: string | null;
  productCode: string | null;
  trustedAt: string;
  lastSeenAt: string;
}

export interface LoginStats {
  periodDays: number;
  successfulLogins: number;
  failedAttempts: number;
  uniqueDevices: number;
  mostUsedDeviceLabel: string | null;
  activeSessions: number;
}

export interface SecurityDashboard {
  stats: LoginStats;
  activity: SecurityActivityItem[];
  trustedDevices: TrustedDevice[];
  securityScore: number;
  mfaEnabled: boolean;
  totpEnabled?: boolean;
  emailEnabled?: boolean;
  lastPasswordChangeAt: string | null;
}
